import React from "react";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable pagination control with a page size selector.
 *
 * Props:
 *  - page: 0-indexed current page
 *  - setPage(newPage)
 *  - totalPages: number of pages (>=1)
 *  - pageSize: items per page
 *  - setPageSize(n): page-size setter (parent should also reset page to 0)
 *  - total: total number of items (for display only)
 *  - sizes: optional list of page-size choices (default [10, 20, 50, 100])
 *  - testId: data-testid prefix
 */
export default function PaginationBar({
  page, setPage, totalPages,
  pageSize, setPageSize,
  total, sizes = [10, 20, 50, 100],
  testId = "pager",
}) {
  if (total <= sizes[0]) {
    // Below the smallest threshold → no pagination needed
    return null;
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-sm" data-testid={`${testId}-bar`}>
      <div className="text-slate-500 text-xs">
        Page {page + 1}/{totalPages} • {total} entrée{total > 1 ? "s" : ""}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Par page</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          className="border rounded-lg px-2 py-1 text-xs bg-white"
          data-testid={`${testId}-size`}
        >
          {sizes.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <Button
          size="sm" variant="outline" className="rounded-full h-8 w-8 p-0"
          disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          size="sm" variant="outline" className="rounded-full h-8 w-8 p-0"
          disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          data-testid={`${testId}-next`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
